<template>
  <div class="green-operation-documents">
    <template v-if="canManageDocuments">
      <div class="section-desc">
        {{ $t('tenancy.greenOperation.uploadHint') }}
      </div>
    </template>
    <div
      v-show="canManageDocuments || (canSendReminder || fileList.length !== 0)"
      class="green-operation-documents__upload-zone"
    >
      <template v-if="canManageDocuments">
        <HKLDDropzone
          v-bind="dropzoneAttrs"
          ref="dropzoneRef"
        />
      </template>
      <HKLLeftRight class="green-operation-documents__upload-actions">
        <template
          v-if="canEditFileList.length"
          #left
        >
          <DocumentsSubmitButtonNModal
            v-if="isTenant"
            ref="submitModal"
            action="submitForReview"
            @onOkClick="submitForReview"
            @open="onOpenSubmitModal"
          />
          <DocumentsSubmitButtonNModal
            v-else-if="canSubmit"
            ref="submitModal"
            action="submitForReview"
            @onOkClick="submitForReview"
            @open="onOpenSubmitModal"
          />
        </template>
        <template
          v-if="!isTenant && canSendReminder"
          #right
        >
          <HKLButton
            color="darkblue"
            action="sendReminder"
            @click="() => openSendReminderModal()"
          />
        </template>
      </HKLLeftRight>
      <HKLForm
        v-show="fileList.length"
        ref="formRef"
      >
        <HKLAttachment
          ref="attachmentRef"
          class="green-operation-documents__attachment-list"
          :class="attachmentClass"
          :sourceId="greenOperation.id"
          v-bind="attachmentAttrs"
          @updateFiles="(files) => fileList = files"
        >
          <template #file-after="{ metaData }">
            <HKLGrayHint
              v-if="hasSubmittedHistory(metaData)"
              class="green-operation-documents__submitted-hint"
              :fontSize="11"
            >
              {{ $t('submittedByOn', [metaData.submittedHistories[0].displayName, parseSubmitDate(metaData.submittedHistories[0].createdAt)]) }}
            </HKLGrayHint>
          </template>
        </HKLAttachment>
      </HKLForm>
    </div>
    <GreenOperationReminderModal
      ref="sendReminderModal"
    />
  </div>
</template>

<script>
import { computed, ref, unref } from 'vue-demi'
import HKLButton from '@/controls/Button/HKLButton.vue'
import HKLForm from '@/controls/Form/HKLForm.vue'
import { useRefForm } from '@/controls/Form/hook'
import HKLGrayHint from '@/controls/HKLGrayHint.vue'
import HKLDDropzone from '@/controls/HkldDropzone.vue'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import HKLAttachment from '@/controls/Upload/HKLAttachment.vue'
import { getTenancyDisplayName } from '@/modules/ccd/tenancy/utils'
import { useAttachments } from '../../../../controls/Upload/hook'
import { useStoreGetters } from '../../../../store'
import { displayI18nFullName } from '../../../../utils/fullName'
import { useAttachmentAttrs, useDropzoneAttrs, useEditStatusFnMap } from './GreenOperationDocuments.hook'
import { hasSubmittedForReview } from './GreenOperationDocuments.utils'
import GreenOperationReminderModal from './GreenOperationReminderModal.vue'
import DocumentsSubmitButtonNModal from './components/DocumentsSubmitButtonNModal.vue'
import { GREEN_OPERATION_DOCUMENT_TYPE, GREEN_OPERATION_SUBMIT_USER_TYPE } from './consts'
import { useGreenOperation, useGreenOperationPerms } from './hook'
import { parseSubmitDate } from './utils'

export default {
  components: {
    HKLDDropzone,
    HKLLeftRight,
    HKLButton,
    HKLForm,
    HKLGrayHint,
    HKLAttachment,
    GreenOperationReminderModal,
    DocumentsSubmitButtonNModal,
  },
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    showExcludedOnly: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const { data: greenOperation, refresh: refreshGreenOperation } = useGreenOperation()
    const sourceId = computed(() => unref(greenOperation).id)
    const { refresh: refreshAttachments } = useAttachments(sourceId)
    const { modal: submitModal, close: closeSubmitModal } = useRefModal()
    const { modal: sendReminderModal, open: openSendReminderModal } = useRefModal()
    const { canSubmit, canSendReminder: originalCanSendReminder } = useGreenOperationPerms()
    const editStatusMapFn = useEditStatusFnMap()
    const dropzoneAttrs = useDropzoneAttrs()
    const attachmentRef = ref()
    const dropzoneRef = ref()
    const showHistoricalExcludedDocuments = computed(() => props.readonly && props.showExcludedOnly)

    const attachmentAttrs = useAttachmentAttrs(props)
    const { isTenant, currentUser } = useStoreGetters('user', ['isTenant', 'currentUser'])
    const { currentTenancy } = useStoreGetters('ccd', ['currentTenancy'])
    const fileList = ref([])
    const canSendReminder = computed(() => !props.readonly && unref(originalCanSendReminder))
    const canManageDocuments = computed(() => !props.readonly && (unref(isTenant) || unref(canSubmit)))
    const canEditFileList = computed(() => {
      if (props.readonly) return []
      return unref(fileList).filter((file) => editStatusMapFn.needSubmit(file))
    })
    const hasSubmittedHistory = (metaData = {}) => {
      return (metaData.submittedHistories || []).length > 0
    }
    const canTenantSubmitFile = (file) => {
      const user = unref(currentUser)
      return user && file.createdBy === user.id && !hasSubmittedForReview(file)
    }
    const canEngineerSubmitFile = (file) => {
      return !hasSubmittedForReview(file) &&
        file.metaData.userType === GREEN_OPERATION_SUBMIT_USER_TYPE.ENGINEER
    }
    const { form: formRef, validate: formValidate } = useRefForm()
    const submitForReview = async () => {
      const user = unref(currentUser)
      const createdAt = new Date()
      const shouldRefreshGreenOperation = unref(fileList).some(file => {
        return unref(isTenant) ? canTenantSubmitFile(file) : canEngineerSubmitFile(file)
      })
      unref(fileList).forEach((file) => {
        const userType = unref(isTenant) ? GREEN_OPERATION_SUBMIT_USER_TYPE.TENANT : GREEN_OPERATION_SUBMIT_USER_TYPE.ENGINEER
        const canSubmitFile = unref(isTenant)
          ? canTenantSubmitFile(file)
          : canEngineerSubmitFile(file)
        if (!canSubmitFile) return {}
        const submittedHistory = {
          id: user.id,
          username: user.username,
          displayName: unref(isTenant) ? getTenancyDisplayName(unref(currentTenancy), user) : (displayI18nFullName(user.firstName, user.lastName) || user.displayName),
          userType,
          createdAt,
        }
        const submittedHistories = (file.metaData.submittedHistories || [])
          .filter(history => history.userType !== userType)
          .concat(submittedHistory)
        if (unref(isTenant)) {
          unref(attachmentRef).update(file.id, {
            EBInfo: file.metaData.type === GREEN_OPERATION_DOCUMENT_TYPE.ELECTRICITY_BILL ? file.metaData.EBInfo : [],
            isSpp: true,
            submittedHistories: [submittedHistory],
          })
          return
        }
        unref(attachmentRef).update(file.id, {
          isSpp: true,
          submittedHistories,
        })
      })
      await unref(attachmentRef).save()
      if (shouldRefreshGreenOperation) {
        await refreshGreenOperation()
      }
      await refreshAttachments()
      unref(dropzoneRef).resetMessages()
    }

    return {
      formRef,
      attachmentRef,
      dropzoneRef,
      isTenant,
      greenOperation,
      canSubmit,
      canSendReminder,
      canManageDocuments,
      sendReminderModal,
      openSendReminderModal,
      attachmentAttrs,
      attachmentClass: computed(() => ({
        'green-operation-documents__attachment-list--historical-excluded': unref(showHistoricalExcludedDocuments),
      })),
      dropzoneAttrs,
      submitForReview,
      submitModal,
      hasSubmittedHistory,
      parseSubmitDate,
      onOpenSubmitModal: () => {
        if (!formValidate()) {
          closeSubmitModal()
        }
      },
      fileList,
      canEditFileList,
      ...editStatusMapFn,
    }
  },
}
</script>

<style lang="scss" scoped>
.section-desc {
  color: #878787;
  font-style: italic;
}
.green-operation-documents {
  // Algin from Fit-out
  &__upload-zone {
    padding: 10px 15px 0 15px;
    border: solid 1px #e5e9eb;
    &:has(.hkld-dropzone) {
      margin-top: 10px;
    }
  }
  &__upload-actions {
    margin-bottom: 10px;
  }
  &__submitted-hint {
    margin-top: 4px;
  }
  &__attachment-list--historical-excluded {
    ::v-deep {
      .hkl-attachment_header th {
        text-align: center;
      }
      .hkl-attachment_header th:first-child,
      .hkl-attachment_row > td:first-child {
        width: calc(100% - 85px);
      }
      .hkl-attachment_header th.exclude,
      .hkl-attachment_row > td.exclude {
        width: 85px;
        min-width: 85px;
        max-width: 85px;
      }
      .hkl-attachment_row {
        & .exclude {
          text-align: center;
        }
      }
      .hkl-attachment_row > td.exclude .hkl-checkbox {
        align-items: center;
        justify-content: center;
      }
      .hkl-checkbox input:disabled {
        cursor: default;
      }
    }
  }
  .hkld-dropzone {
    &:has(.hkld-dropzone__uploading),
    &:has(.hkld-dropzone__upload-result),
    &:has(.hkld-dropzone__error-message) {
      margin-bottom: 10px;
    }
  }

}
</style>
